import { gql, useQuery } from "@apollo/client";
import Link from "next/link";
import { useEffect, useState } from "react";
import { ProductAvailability } from "../../../../graphql/operations";
import { addDuration, formatDate } from "../../../../utils/calendarUtils";
import { getCurrency } from "../../../../utils/getCurrency";
import Loader from "../../../Atoms/Loader";
import Price from "../../../Atoms/Price";
import WaitingListButton from "../../Product/Parts/ProductWaitingListButton";
import SumoCalendar from "../SumoCalendar";
import styles from "./SumoCalendar.module.scss";

export default function SumoAvailability({
  ids,
  selectedProduct,
  setSelectedProduct,
  setOpenSumoProducts,
  openSumoProducts,
}) {
  const currency = getCurrency();
  const [selectedDate, setSelectedDate] = useState(null);
  const now = new Date();
  const until = addDuration(now, { months: 3 });

  const { loading, error, data } = useQuery(GET_SUMO_AVAILABILITY, {
    fetchPolicy: "cache-and-network",
    ssr: false,
    variables: {
      input: {
        ids: ids,
      },
      fromDate: formatDate(now),
      untilDate: formatDate(until),
      currency: currency,
    },
  });

  useEffect(() => {
    if (data && selectedDate) {
      const records = data.products.records;
      const findOpenSumo = records
        .map((p) => {
          if (
            p.availability.find(
              (a) =>
                a.date === formatDate(selectedDate) && a.status === "AVAILABLE"
            )
          ) {
            return p.id;
          }
        })
        .filter((result) => result !== undefined);
      setOpenSumoProducts(findOpenSumo);
    }
  }, [selectedDate, data, setOpenSumoProducts]);

  useEffect(() => {
    if (
      openSumoProducts.length > 0 &&
      !openSumoProducts.includes(selectedProduct)
    ) {
      setSelectedProduct(openSumoProducts[0]);
    }
  }, [openSumoProducts, selectedProduct, setSelectedProduct]);

  if (loading) {
    return <Loader />;
  }

  if (error || !data) {
    return <></>;
  }

  const { products } = data;
  const { records } = products;

  const UnavailableTourMessageContainer = () => {
    return records[0].id ? (
      <div className={styles.NoAvailabilityText}>
        <WaitingListButton
          productId={selectedProduct || records[0].id}
          productOptions={
            records.find((r) => r.id === selectedProduct) || records[0]
          }
        >
          Currently not available
        </WaitingListButton>
      </div>
    ) : (
      <p style={{ textAlign: "center" }} className={styles.NoAvailabilityText}>
        Currently not available
      </p>
    );
  };

  const availability = records
    .map((obj) => obj.availability)
    .flat(1) as ProductAvailability[];
  const isAvailable =
    availability.filter((a) => a.status === "AVAILABLE").length > 0;

  return (
    <div className={styles.SumoCalendarContainer}>
      {isAvailable ? (
        <>
          <SumoCalendar
            availability={availability}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
          <p
            style={{
              fontSize: 10,
              textAlign: "left",
              color: "#444",
              marginBottom: 15,
            }}
          >
            Dates are shown in{" "}
            <b className="ReservationDatepickerTimeZone">
              {products.records[0]?.location.country.name} Standard Time
            </b>
          </p>
          {openSumoProducts.length > 0 && (
            <>
              <Price
                id={selectedProduct || openSumoProducts[0]}
                price={
                  selectedProduct
                    ? records.find((r) => r.id === selectedProduct).price
                    : records.find((r) => r.id === openSumoProducts[0]).price
                }
              />
              <Link
                href={{
                  pathname: `/product/${selectedProduct}`,
                  query: { date: formatDate(selectedDate) },
                }}
                className={styles.CheckDetailsBtn}
              >
                Check Details
              </Link>
            </>
          )}
        </>
      ) : (
        <UnavailableTourMessageContainer />
      )}
    </div>
  );
}

const GET_SUMO_AVAILABILITY = gql`
  query (
    $input: ProductsInput
    $fromDate: Date
    $untilDate: Date
    $currency: Currency!
  ) {
    products(input: $input) {
      records {
        id
        location {
          country {
            name
          }
        }
        price(currency: $currency) {
          amount
          currency
        }
        availability(fromDate: $fromDate, untilDate: $untilDate) {
          date
          status
          timeSlots {
            status
          }
        }
      }
    }
  }
`;
